/**
 * TypeScript State Utilities for Guitar Arrangements
 * Pure utility functions for working with guitar arrangement state
 */

import { 
  GuitarArrangement,
  DEFAULT_ROOT_CONFIG,
  DEFAULT_CAGED_SHAPES,
  DEFAULT_CAGED_VISUALIZATIONS,
  createDefaultGuitarArrangement
} from '../presets/presetSchema';
import { generateGuitarId } from '../utils/idUtils';

// ================================================================================
// GUITAR FACTORY & UTILITIES
// ================================================================================

/**
 * Create a new guitar arrangement with intelligent defaults
 */
export function createGuitar(partial: Partial<GuitarArrangement> = {}): GuitarArrangement {
  const id = partial.id || generateGuitarId();
  
  // Use centralized defaults from schema
  const defaults = createDefaultGuitarArrangement();
  
  return {
    id,
    root: {
      ...DEFAULT_ROOT_CONFIG,
      ...partial.root
    },
    overlay: partial.overlay || defaults.overlay || undefined,
    caged: partial.caged || {
      activeShapes: { ...DEFAULT_CAGED_SHAPES },
      quality: 'MAJOR',
      visualizations: { ...DEFAULT_CAGED_VISUALIZATIONS }
    }
  };
}

/**
 * Update an existing guitar arrangement (immutable)
 */
export function updateGuitar(
  guitar: GuitarArrangement, 
  updates: Partial<GuitarArrangement>
): GuitarArrangement {
  return {
    ...guitar,
    ...updates,
    root: {
      ...guitar.root,
      ...updates.root
    },
    overlay: 'overlay' in updates ? updates.overlay : guitar.overlay,
    caged: 'caged' in updates ? updates.caged : guitar.caged
  };
}

/**
 * Clone a guitar arrangement to prevent mutations
 */
export function cloneGuitar(guitar: GuitarArrangement): GuitarArrangement {
  return JSON.parse(JSON.stringify(guitar));
}

// ================================================================================
// ARRAY OPERATIONS
// ================================================================================

/**
 * Add a guitar to an arrangement list
 */
export function addGuitarToArrangement(
  guitars: GuitarArrangement[], 
  guitar?: Partial<GuitarArrangement>
): GuitarArrangement[] {
  const newGuitar = createGuitar(guitar);
  return [...guitars, newGuitar];
}

/**
 * Remove a guitar from an arrangement list
 */
export function removeGuitarFromArrangement(
  guitars: GuitarArrangement[], 
  id: string
): GuitarArrangement[] {
  return guitars.filter(guitar => guitar.id !== id);
}

/**
 * Update a guitar in an arrangement list
 */
export function updateGuitarInArrangement(
  guitars: GuitarArrangement[], 
  id: string, 
  updates: Partial<GuitarArrangement>
): GuitarArrangement[] {
  return guitars.map(guitar => 
    guitar.id === id ? updateGuitar(guitar, updates) : guitar
  );
}

/**
 * Reorder guitars in an arrangement
 */
export function reorderGuitars(
  guitars: GuitarArrangement[], 
  fromIndex: number, 
  toIndex: number
): GuitarArrangement[] {
  const result = [...guitars];
  const [removed] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, removed);
  return result;
}
